import { SECTION_TYPES } from '@/content/values/sections';
import { Section } from '@/courses/models/section.model';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { HOMEWORKS_EVENTS, HOMEWORK_STATUS, _HOMEWORKS_STORE } from '@/homework/values/homeworks.values';
import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { ANNOTATION_TYPES } from '../values/courses.values';
import { HomeworkMixin } from '@/homework/mixins/homework.mixin';
import ToolService from '@/providers/tools';

@Component({})
export default class ResponsePdfDrawnMixin extends Mixins(HomeworkMixin) {

  @Prop() readonly section: any;
  @Prop() readonly student: any;
  @Prop() readonly history: any;

  public response: Section = null;
  public isSaving: boolean = false;
  public pdfLoaded: boolean = false;
  public pdfChanged: boolean = false;
  public pagesDrawn: number[] = [];
  public isSavedDraft = {
    pagesDrawn: [],
    status: false
  };
  public grades: any = null;

  get courseId(): number {
    return this.section.courseId;
  }

  get classId(): number {
    return this.course.classId;
  }

  get hasPagesDrawn(): boolean {
    return this.pagesDrawn.length > 0;
  }

  get canSendFeedbackDraft() {
    if(this.pdfLoaded && !this.isSaving) {
      if(this.feedbackDraft) {
        if(this.isSavedDraft.status && this.pdfChanged) { 
          return true;
        } else if (this.hasPagesDrawn) {
          return true;
        }
      } else if(this.hasPagesDrawn) {
        return true;
      } else if(this.isSavedDraft.status && this.pdfChanged) {
        return true;
      }
    }
    return false;
  }

  get feedbackDraft() {
    return this.section.homeworkDraftAssetInfo ? this.section.homeworkDraftAssetInfo : null;
  }

  get pdfUrl(): string {
    if(this.feedbackDraft) {
      return this.feedbackDraft.url;
    } else {
      return this.section.assetsInfo[0].pdfUrl ? this.section.assetsInfo[0].pdfUrl : this.section.assetsInfo[0].url;
    }
  }

  get canSendFeedback(): boolean {
    return this.section.status && this.section.status != HOMEWORK_STATUS.GRADED && this.section.status != HOMEWORK_STATUS.FINISHED
        && this.section.status != HOMEWORK_STATUS.DRAFT;
  }

  get canSubmit() {
    if(this.pdfLoaded && !this.isSaving) {
      if(this.feedbackDraft) {
        return true;
      } else if (this.hasPagesDrawn) {
        return true;
      }
    }
    return false;
  }

  get homeworkId() {    
    return this.section['section'].homeworkId;
  }

  onChangePdf({drawn, pagesDrawn}) {
    this.pdfChanged = true;
    this.pagesDrawn = pagesDrawn;
    this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.CHANGE_CONFIRM_CLOSE_HOMEWORK_VIEWER, true);
  }

  onLoadPdf() {
    this.pdfLoaded = true;
  }

  confirmSendFeedback() {
    this.$EdModal.createGradeModal({ 
      action: (res: any) => {
        this.$EdToast.create({ msg: `${this.$i18n.t('common.sendingFeedback')}...` });
        setTimeout(() => {
          this.grades = res.grades;
          this.sendFeedback();
        }, 400);
      }
    });
  }

  public async confirmSendWhiteboardFeedback(boardGroupHash: string) {   
    let usersMadeAnnotations = await this.obtainUsersMadeAnnotations(boardGroupHash);

    if(!usersMadeAnnotations || usersMadeAnnotations.length === 0){
      this.openModalWithoutAnnotations();
      return;
    }
    this.$EdModal.createGradeModal({ 
      homeworkId: this.homeworkId,
      courseId: this.courseId,
      section: this.section,
      hasGrading: this.course.hasGrading,
      action: (res: any) => {
        this.$EdToast.create({ msg: `${this.$i18n.t('common.sendingFeedback')}...` });
        setTimeout(() => {
          this.grades = res.grades;
          this.sendWhiteboardHomeworkFeedback();
        }, 400);
      }
    });
  }

  async getFeedbackRequest() {
    let pdfDraw: any = this.$refs.EdPdfDraw;
    let file = await pdfDraw.getCurrentPdf();
    return this.buildRequest(file, SECTION_TYPES.LESSON_PDF.code);
  }

  private getWhiteBoardFeedbackRequest() {
    return this.buildRequest(null, SECTION_TYPES.LESSON_BOARD.code);
  }

  private buildRequest(file: any, type: string): any{
    let section: any = this.section;
    let data: any = { 
      req: {
        name: this.section.name,
        id: section.id,
        type: type,
        classId: this.student.classId
      }, 
      student: this.student,
      file
    }
    
    if(this.grades) {
      if(this.course.hasGrading) {
        data.req.grades = this.grades;
      } else {
        data.req.grade = this.grades;
      }
    }
    return data;
  }

  async sendFeedback() {
    try {
      this.isSaving = true;
      let request = await this.getFeedbackRequest();
      await this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.SEND_FEEDBACK_TO_STUDENT, request);
      this.section.status = HOMEWORK_STATUS.GRADED;
      this.$edEventBus.$emit(HOMEWORKS_EVENTS.UPDATE_HOMEWORK_STATUS, this.section);
      this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.UPDATE_WIDGET_TEACHER_PERSON, { 
        student: this.student, 
        homework: {...this.section, homeworkAssetInfo: this.section.assetsInfo[0] }
      });
      this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.REFRESH_PERSON_STATUS, {student: this.student, isReload: false});
      this.$EdToast.update({ 
        msg: `${this.$i18n.t('common.sent')}`,
        type: TOAST_TYPES.SUCCESS,
        timeout: 1000
      });
      setTimeout(() => {
        this.isSaving = false;
      }, 1300);
      this.$EdModal.closeModal();
    } catch(err) {
      this.isSaving = false;
      this.$EdToast.hide();
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    }
  }

  async setFeedbackDraftAndExit() {
    await this.sendFeedbackDraft();
    this.$EdModal.closeModal();
  }

  async sendFeedbackDraft() {
    if(this.canSendFeedbackDraft) {
      try {
        this.isSaving = true;
        let req = await this.getFeedbackDraftRequest();
        this.$EdToast.create({ msg: `${this.$i18n.t('common.saving')}...` });
        await this.setFeedbackDraft(
          this.student,
          this.courseId,
          req.homework,
          req.file
        );
        this.pdfChanged = false;
        if(!this.isSavedDraft.status) {
          this.isSavedDraft.status = true;
          this.isSavedDraft.pagesDrawn = this.pagesDrawn;
        }
        this.isSaving = false;
      } catch(err) {
        this.isSaving = false;
        this.$EdToast.hide();
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('courses.errors.errorTitle'),
          body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
          type: KEYS.EVENT_TYPES.DANGER
        });
      }
    }
  }

  async getFeedbackDraftRequest() {
    let pdfDraw: any = this.$refs.EdPdfDraw;
    let original = this.feedbackDraft ? this.feedbackDraft.hash : null;
    let currentPagesDrawn = pdfDraw.getDrawnPages();
    let pages = [];

    /* We check if the previous draft was changed */
    if(this.isSavedDraft.status) {
      currentPagesDrawn.forEach(p => {
        let found = this.isSavedDraft.pagesDrawn.findIndex(pd => pd == p) !== -1;
        if(!found) this.isSavedDraft.pagesDrawn.push(p);
      });
      pages = ToolService.orderByNumber(this.isSavedDraft.pagesDrawn);
    } else {
      pages = currentPagesDrawn;
    }

    let request: any = {
      homework: {
        name: this.section.name,
        classId: this.student.classId,
        sectionId: this.section.section.id,
        type: SECTION_TYPES.LESSON_PDF.code,
        responseOwnerId: this.student.id,
        metadata: {
          type: ANNOTATION_TYPES.PDF_ANNOTATION,
          pages: pages,
        }
      }, 
      file: pdfDraw.getPDFPages(pages)
    }
    if(original) request.homework.metadata.original = original;
    return request;
  }

  public sendWhiteboardHomeworkFeedback(){
    let request = this.getWhiteBoardFeedbackRequest();    
    this.isSaving = true;
    this.$store.dispatch(_HOMEWORKS_STORE.ACTIONS.SEND_FEEDBACK_TO_STUDENT, request)
      .then(response => {        
        this.section.status = HOMEWORK_STATUS.GRADED;        
        this.$edEventBus.$emit(HOMEWORKS_EVENTS.REALOAD_TEACHER_HOMEWORKS_FEEDBACK, {studentId: this.student.id, courseId: this.courseId});

        this.$EdToast.update({ 
          msg: `${this.$i18n.t('common.sent')}`,
          type: TOAST_TYPES.SUCCESS,
          timeout: 1000
        });
        this.$EdModal.closeModal();
      }).catch(error => {
        this.$EdToast.hide();
        this.$EdModal.createMessageModal({
          title: this.$i18n.t('courses.errors.errorTitle'),
          body: this.$i18n.t('courses.errors.errorResponseWriteObject'),
          type: KEYS.EVENT_TYPES.DANGER
        });
      }).finally(() => this.isSaving = false);
  }

  beforeDestroy() {
    this.$edEventBus.$off(COURSES_EVENTS.SAVE_AND_CLOSE_HOMEWORK, this.setFeedbackDraftAndExit);
  }
  
  created() {
    this.$edEventBus.$on(COURSES_EVENTS.SAVE_AND_CLOSE_HOMEWORK, this.setFeedbackDraftAndExit);
  }

}